<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import Logo from '@components/logo'
import { useAuthStore } from '@state/stores/auth'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useChannelsStore } from '@state/stores/channels'
import { useAnalytics } from '@composables/use-analytics'
import { LIBRARY_ROUTES, LIBRARY_ROUTE_PREFIX } from '@utils/library-routes'

const authStore = useAuthStore()
const channelsStore = useChannelsStore()
const appearanceStore = useAppearanceStore()
const memberStore = useMemberStore()
const premiumStore = usePremiumStore()
const route = useRoute()
const analyticsService = useAnalytics()

const loggedIn = computed(() => authStore.loggedIn)
const currentMember = computed(() => memberStore.currentMember)
const email = computed(() => currentMember.value?.email)
const isPremium = computed(() => premiumStore.isPremium)
const entitledFreeTrial = computed(() => !premiumStore.hasEverBeenPremium)
const isMenuOpen = computed(() => appearanceStore.isMainMenuOpen)
const isLibraryActive = computed(() =>
    Boolean(route.name?.startsWith(LIBRARY_ROUTE_PREFIX))
)

function onPremiumUpsellClick() {
    analyticsService.premiumUpsellInitiated('sidebar')
}

function closeMainMenu() {
    appearanceStore.closeMainMenu()
}

watch(() => route.fullPath, closeMainMenu)

onMounted(() => {
    addEventListener('resize', closeMainMenu, { passive: true })
})

onBeforeUnmount(() => {
    removeEventListener('resize', closeMainMenu)
})
</script>

<template>
    <div
        :is-nav-open="isMenuOpen"
        :class="['side-bar', { 'active-nav': isMenuOpen }]"
    >
        <div class="logo-wrapper">
            <Logo :has-margin="false" />
        </div>

        <BaseLink
            v-if="!loggedIn"
            class="item base-link login-link"
            name="login"
        >
            <div class="link">Log in</div>
        </BaseLink>

        <BaseLink
            v-if="loggedIn && !isPremium"
            :to="{ name: 'premium/view-plans', query: { from: 'sidebar' } }"
            class="premium-link"
        >
            <BaseButton
                button-type="highlight"
                size="xsmall"
                :modifiers="{ fill: true, noMargins: true }"
                @click="onPremiumUpsellClick"
            >
                {{ entitledFreeTrial ? 'START FREE PREMIUM TRIAL' : 'UPGRADE TO PREMIUM' }}
            </BaseButton>
        </BaseLink>

        <div class="section">
            <BaseLink
                class="title base-link"
                :to="{ name: 'home' }"
                name="home"
            >
                <BaseIcon
                    class="icon menu-icon icon-home"
                    name="home"
                />
                <span class="header">Home</span>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'browse/charts/index' }"
                :to="{ name: 'browse/charts/index' }"
                name="browse/charts/index"
            >
                <div class="link">Charts</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'browse/playlists/index' }"
                :to="{ name: 'browse/playlists/index' }"
            >
                <div class="link">Playlists</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'browse/genres' }"
                :to="{ name: 'browse/genres' }"
            >
                <div class="link">Genres & Moods</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: ['browse/mixes/latest', 'browse/mixes/trending'].includes($route.name) }"
                :to="{ name: 'browse/mixes/latest' }"
            >
                <div class="link">Mixes</div>
            </BaseLink>
        </div>

        <div class="section section-search">
            <BaseLink
                class="title base-link"
                :to="{ name: 'search' }"
            >
                <BaseIcon
                    class="icon menu-icon"
                    name="search"
                />
                <span class="header">Search</span>
            </BaseLink>
        </div>

        <div class="section">
            <BaseLink
                class="title base-link"
                :to="{ name: 'channel-index' }"
                name="radio"
            >
                <BaseIcon
                    class="icon menu-icon"
                    name="radio"
                />
                <span class="header">Radio</span>
            </BaseLink>

            <BaseLink
                v-for="channelLink in channelsStore.channels"
                :key="channelLink.id"
                class="item base-link"
                :class="{ active: channelLink.id === $route.params.channelId }"
                :to="{ name: 'channel', params: { channelId: channelLink.id } }"
                name="channel"
            >
                <div class="link">{{ channelLink.title }}</div>
            </BaseLink>
        </div>

        <div class="section">
            <BaseLink
                class="title base-link"
                :class="{ active: isLibraryActive }"
                :to="{ name: LIBRARY_ROUTES.landing }"
            >
                <BaseIcon
                    class="icon menu-icon"
                    name="heart"
                    filled
                />
                <span class="header">Your Library</span>
            </BaseLink>
        </div>

        <div class="section section-account">
            <div v-if="loggedIn">
                <BaseButton
                    button-type="highlight"
                    size="xsmall"
                    :modifiers="{ fill: true, noMargins: true }"
                    @click="() => {}"
                >{{ email }}</BaseButton>

                <br >

                <BaseLink
                    class="item base-link"
                    :class="{ active: $route.name === 'account' }"
                    name="account/overview"
                >
                    <div class="link">Account Management</div>
                </BaseLink>

                <!--
                    `name="settings"` still points at /settings, which redirects to the
                    default tab — but that means $route.name is only ever a SUB-route, so
                    the highlight has to match the cluster, not the redirect's own name.
                -->
                <BaseLink
                    class="item base-link"
                    :class="{ active: ['settings/audio-quality', 'settings/external-player'].includes($route.name) }"
                    name="settings"
                >
                    <div class="link">Settings</div>
                </BaseLink>

                <BaseLink
                    class="item base-link"
                    name="logout"
                >
                    <div class="link">Log out</div>
                </BaseLink>
            </div>
        </div>
        <div class="section section-about">
            <div class="separator"/>
            <div class="title">
                <span class="header">Discover FRISKY</span>
            </div>
            <a
                class="item base-link"
                href="https://frisky.fm/about"
                target="_blank"
            >
                <div class="link">About</div>
            </a>
            <a
                class="item base-link"
                href="https://apps.apple.com/us/app/frisky-electronic-music/id524174564"
                target="_blank"
            >
                <div class="link">FRISKY iOS</div>
            </a>
            <a
                class="item base-link"
                href="https://friskyfm.myshopify.com/"
                target="_blank"
            >
                <div class="link">Store</div>
            </a>
            <a
                class="item base-link"
                href="https://frisky.fm/support"
                target="_blank"
            >
                <div class="link">Support</div>
            </a>
            <a
                class="item base-link"
                href="https://frisky.fm/about/privacy-policy"
                target="_blank"
            >
                <div class="link">Privacy Policy</div>
            </a>
            <a
                class="item base-link"
                href="https://frisky.fm/about/terms"
                target="_blank"
            >
                <div class="link">Terms of Use</div>
            </a>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use 'sass:math';
@use '@design' as *;

.menu-button {
    position: absolute;
    top: math.div($size-menu-bar-height, 2);
    left: 30px;
    cursor: pointer;
    transform: translateY(-50%);
}

.icon {
    width: 24px;
    height: 24px;
    margin-right: $s-xxs;
    vertical-align: middle;
    color: $c-white;

    &-home {
        width: 20px;
        height: 20px;
    }
}

.menu-icon {
    display: none;
}

.header {
    vertical-align: middle;
}

.side-bar {
    position: fixed;
    left: 0;
    z-index: $layer-modal-z-index;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: $size-side-bar;
    height: calc(100% - #{$size-music-player + $size-menu-bar-height});
    padding: $s-xxl $s-xxl $s-xxxl * 2;
    overflow-y: auto;
    background: $c-surface-chrome;
    transform: translateX(-100%);

    @include transition(transform);

    &.active-nav {
        transform: translateX(0);
    }

    &::-webkit-scrollbar-thumb {
        background-color: $c-border-default;
    }
}

.section {
    margin-top: $s-l;

    &-search {
        display: none;
    }
}

.logo-wrapper {
    display: none;
}

.title {
    display: block;
    margin-bottom: 15px;
    font-size: 14px;
    font-weight: 800;
    line-height: 18px;
    letter-spacing: -0.5px;

    &, &:visited {
        color: $c-white;
    }

    // Section titles are white/bold by default, so the active state needs its
    // own colour to be visible (items get `.item.active .link` instead).
    &.active {
        .header {
            color: $c-highlight;
        }

        .icon {
            color: $c-highlight;
        }
    }
}

.link {
    display: block;
    font-size: 13px;
    font-weight: 500;
    line-height: 36px;
    vertical-align: middle;
    color: $c-text-faded;
    letter-spacing: -0.09px;
    text-decoration: none;

    &:hover {
        color: $c-accent-default;
    }
}

.base-link {
    &:hover,
    &:active {
        text-decoration: none;
    }
}

.item {
    display: flex;
    flex: 1 1 auto;

    &.active .link {
        font-weight: bold;
        color: $c-white;
    }

    &:last-of-type {
        margin-bottom: $s-m;
    }
}

.login-link {
    // Sits directly in the .side-bar flex column; without this, .item's
    // `flex: 1 1 auto` stretches it to fill tall screens and pushes the nav down.
    flex: 0 0 auto;
}

.text-email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    margin: $s-xl 0;
    background: $c-surface-overlay;
}

@include desktop {
    .premium-link {
        display: none;
    }

    .login-link {
        display: none;
    }

    .side-bar {
        position: sticky;
        top: 0;
        z-index: auto;
        width: $size-large-side-bar;
        height: calc(100vh - #{$size-music-player});
        padding-top: $s-xl;
        padding-bottom: $s-xxl;
        transform: translateX(0);
    }

    .menu-button {
        display: none;
    }

    .menu-icon {
        display: unset;
    }

    .logo-wrapper {
        display: block;
        margin-bottom: $s-xl;
    }

    .item {
        &:last-of-type {
            margin-bottom: 0;
        }
    }

    .section {
        &-search {
            display: block;
        }

        &-account {
            display: none;
        }
    }
}

@media screen and (min-height: 1000px) {
    .section {

        &-about {
            position: absolute;
            bottom: $s-xxl;
        }
    }
}
</style>
